import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { sessionData } from "../helpers/session-data";
import { myProfileService } from "../services/myProfile.service";
import type {
  MyProfileUserDetails,
  MyProfilePhoneItem,
  MyProfileAddressItem,
  MyProfileIdProof,
  TermsItem,
  LoginActivityItem,
} from "../components/MainContent/pages/MyProfile/types";

export interface MyProfileState {
  userDetails: MyProfileUserDetails | null;
  phoneNumbers: MyProfilePhoneItem[];
  addresses: MyProfileAddressItem[];
  idProofs: MyProfileIdProof[];
  termsAndConditions: TermsItem[];
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
}

const initialState: MyProfileState = {
  userDetails: null,
  phoneNumbers: [],
  addresses: [],
  idProofs: [],
  termsAndConditions: [],
  isLoading: false,
  isRefreshing: false,
  error: null,
};

export const fetchMyProfile = createAsyncThunk(
  "myProfile/fetchMyProfile",
  async (options: { isRefresh?: boolean } | void, { rejectWithValue }) => {
    const payload = sessionData();
    const { business_id, device_token, session_id } = payload;
    if (!device_token || !session_id || !business_id) {
      return rejectWithValue("Session not available");
    }

    const sessionPayload = {
      device_token,
      session_id,
      business_id,
    };

    const [userDetails, phoneNumbers, addresses, idProofs, termsAndConditions] = await Promise.all([
      myProfileService.getUserDetails(sessionPayload),
      myProfileService.getUserPhoneNumbers(sessionPayload),
      myProfileService.getUserAddresses(sessionPayload),
      myProfileService.getUserIdProofs(sessionPayload),
      myProfileService.getTermsStatusCheck(sessionPayload),
    ]);

    const idProofsEnriched = myProfileService.enrichIdProofsWithUserDetails(
      idProofs ?? [],
      userDetails
    );

    return {
      userDetails,
      phoneNumbers: phoneNumbers ?? [],
      addresses: addresses ?? [],
      idProofs: idProofsEnriched,
      termsAndConditions: termsAndConditions ?? [],
    };
  }
);

const myProfileSlice = createSlice({
  name: "myProfile",
  initialState,
  reducers: {
    clearMyProfileError: (state) => {
      state.error = null;
    },
    clearMyProfile: () => initialState,
    setProfilePicturePath: (state, action: PayloadAction<string>) => {
      if (state.userDetails) {
        state.userDetails.profile_picture = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyProfile.pending, (state, action) => {
        const arg = action.meta.arg;
        const isRefresh =
          arg !== undefined && arg !== null && typeof arg === "object" && arg.isRefresh === true;
        state.isRefreshing = isRefresh;
        if (!isRefresh) state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchMyProfile.fulfilled,
        (
          state,
          action: PayloadAction<{
            userDetails: MyProfileUserDetails | null;
            phoneNumbers: MyProfilePhoneItem[];
            addresses: MyProfileAddressItem[];
            idProofs: MyProfileIdProof[];
            termsAndConditions: TermsItem[];
          }>
        ) => {
          state.isLoading = false;
          state.isRefreshing = false;
          const previousProfilePicture = state.userDetails?.profile_picture;
          state.userDetails = action.payload.userDetails;
          if (state.userDetails && previousProfilePicture && !state.userDetails.profile_picture) {
            state.userDetails.profile_picture = previousProfilePicture;
          }
          state.phoneNumbers = action.payload.phoneNumbers;
          state.addresses = action.payload.addresses;
          state.idProofs = action.payload.idProofs;
          state.termsAndConditions = action.payload.termsAndConditions;
          state.error = null;
        }
      )
      .addCase(fetchMyProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.isRefreshing = false;
        state.error =
          (action.payload as string) ?? action.error.message ?? "Failed to load profile";
      });
  },
});

export const { clearMyProfileError, clearMyProfile, setProfilePicturePath } =
  myProfileSlice.actions;

export function selectUserManagementAll(state: {
  myProfile?: MyProfileState | null;
}): LoginActivityItem[] {
  const um = state.myProfile?.userDetails?.user_management;
  return Array.isArray(um) ? um : [];
}

export const selectMyProfileUserDetails = (state: { myProfile?: MyProfileState | null }) =>
  state.myProfile?.userDetails ?? null;

export const selectMyProfilePhoneNumbers = (state: { myProfile?: MyProfileState | null }) =>
  state.myProfile?.phoneNumbers ?? [];

export const selectMyProfileAddresses = (state: { myProfile?: MyProfileState | null }) =>
  state.myProfile?.addresses ?? [];

export const selectMyProfileIdProofs = (state: { myProfile?: MyProfileState | null }) =>
  state.myProfile?.idProofs ?? [];

export const selectMyProfileTerms = (state: { myProfile?: MyProfileState | null }) =>
  state.myProfile?.termsAndConditions ?? [];

export const selectMyProfileIsLoading = (state: { myProfile?: MyProfileState | null }) =>
  state.myProfile?.isLoading ?? false;

export const selectMyProfileIsRefreshing = (state: { myProfile?: MyProfileState | null }) =>
  state.myProfile?.isRefreshing ?? false;

export const selectMyProfileError = (state: { myProfile?: MyProfileState | null }) =>
  state.myProfile?.error ?? null;

export default myProfileSlice.reducer;
